import { WarningSchema } from "@/schemas/warning-data-schema";
import { ITimelineEvents, ITimelineFormatted } from "@/types/timeline-type";
import {
	getColorStatusByColor,
	getWarningIconByValueAndLayer,
} from "./get-color-by-value";

export const SECOND_IN_A_DAY = 86400;

const getSecondsSinceMidnight = (date: Date) => {
	const midnight = new Date(date);
	midnight.setUTCHours(0, 0, 0, 0);
	return Math.floor((date.getTime() - midnight.getTime()) / 1000);
};

function mergeSlots(slots: ITimelineFormatted[]): ITimelineFormatted[] {
	const events: {
		time: number;
		type: "start" | "end";
		slot: ITimelineFormatted;
	}[] = [];

	for (const slot of slots) {
		const start = slot.startDateSecond;
		const end = start + slot.durationSecond;

		events.push({ time: start, type: "start", slot });
		events.push({ time: end, type: "end", slot });
	}

	events.sort((a, b) => a.time - b.time);

	const active: ITimelineFormatted[] = [];
	const result: ITimelineFormatted[] = [];

	for (let i = 0; i < events.length - 1; i++) {
		const event = events[i];

		if (event.type === "start") {
			active.push(event.slot);
		} else {
			const index = active.indexOf(event.slot);
			if (index !== -1) active.splice(index, 1);
		}

		const nextTime = events[i + 1].time;

		if (active.length > 0 && nextTime > event.time) {
			const maxSlot = active.reduce((max, s) =>
				s.valueNumber > max.valueNumber ? s : max,
			);

			result.push({
				...maxSlot,
				startDateSecond: event.time,
				durationSecond: nextTime - event.time,
			});
		}
	}

	return result;
}

export const buildTimeline = (
	timelineData: WarningSchema[],
): ITimelineEvents[] => {
	const grouped = new Map<string, WarningSchema[]>();

	for (const item of timelineData) {
		const layer = item.properties.layer;

		if (!grouped.has(layer)) {
			grouped.set(layer, []);
		}

		grouped.get(layer)?.push(item);
	}

	return Array.from(grouped.entries()).map(([event, items]) => {
		const maxValue =
			items.length > 0 ? Math.max(...items.map((w) => w.properties.value)) : 0;

		const objectIcon = getWarningIconByValueAndLayer({
			event,
			value: maxValue,
		});

		const rawSegments: ITimelineFormatted[] = items.map((item) => {
			const start = new Date(item.properties.effective);
			const end = new Date(item.properties.expires);

			const startSeconds = getSecondsSinceMidnight(start);
			const endSeconds = getSecondsSinceMidnight(end);

			return {
				startDateSecond: startSeconds,
				durationSecond: endSeconds - startSeconds,
				startDateToDisplay: start.toISOString(),
				endDateToDisplay: end.toISOString(),
				color: getColorStatusByColor(item.properties.value) ?? "green",
				icon: getWarningIconByValueAndLayer({
					event: item.properties.layer,
					value: item.properties.value,
				}),
				severity: item.properties.severity,
				valueNumber: item.properties.value,
				event,
				instructionUs: item.properties["instruction_en-US"],
				instructionAr: item.properties["instruction_ar-AR"],
			};
		});

		const mergedSegments = mergeSlots(rawSegments);

		return {
			event,
			value: 86400,
			slots: mergedSegments,
			icon: objectIcon,
		};
	});
};
